import { useState, useEffect, useRef, useCallback } from "react";
import THEME from "./theme";

// RFID-Pruefliste (CARE-Sync, nur Administratoren) - eigenstaendige Komponente.
// Manuelle Pruefliste fuer Bestandsmitglieder: Faelle, die die CARE-Bestands-Automatik bewusst NICHT
// selbst entscheidet (Backend: /api/care-sync/bestand/*, siehe routers_care_sync.py). Zeigt nur
// maskierte RFID-Nummern - genau das, was das Backend liefert.
// Bewusst OHNE Importe aus App.js (nur React + theme.js), damit die Datei unabhaengig vom Stand der
// App.js ausgeliefert werden kann. Einbindung in App.js: Reiter "RFID-Pruefliste" in EinstellungenSeite
// (nur Rolle "admin"); Prop onPersonOeffnen(typ, id) ist optional (Link zum Mitgliedsprofil).

const API = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ? "http://localhost:8000"
  : `${window.location.protocol}//${window.location.hostname}`;
const apiFetch = (url, optionen = {}) => fetch(url, { ...optionen, credentials: "include" });

// Lokale Kopie der benoetigten Basis-Styles (entspricht s.* am Ende von App.js)
const st = {
  input: { width: "100%", padding: "14px 16px", borderRadius: THEME.radius.md, border: `1px solid ${THEME.colors.border}`, background: THEME.colors.background, color: THEME.colors.text, fontSize: THEME.fontSize.lg, boxSizing: "border-box" },
  btn: { padding: "14px 24px", borderRadius: THEME.radius.md, background: THEME.colors.primary, color: kontrasttext(THEME.colors.primary), fontSize: THEME.fontSize.lg, fontWeight: 600, border: "none", cursor: "pointer", width: "100%" },
  btnGray: { padding: "14px 24px", borderRadius: THEME.radius.md, background: THEME.colors.surfaceMuted, color: kontrasttext(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.lg, border: "none", cursor: "pointer" },
  karte: { background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "1.75rem", borderLeft: `4px solid ${THEME.colors.primary}` },
  tabelle: { background: THEME.colors.surface, borderRadius: THEME.radius.xl, overflow: "hidden" },
  tabelleHeader: { display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1fr 1.2fr 1fr 1fr", padding: "18px 24px", background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" },
  tabelleZeile: { display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1fr 1.2fr 1fr 1fr", padding: "20px 24px", borderTop: `1px solid ${THEME.colors.border}`, alignItems: "center" },
  modalBg: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modal: { background: THEME.colors.surface, borderRadius: THEME.radius.xxxl, padding: "2.75rem", width: THEME.sizes.modalMaxWidth, maxHeight: "90vh", overflowY: "auto" },
};

function kontrasttext(hexFarbe) {
  const hex = (hexFarbe || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#111827" : "#ffffff";
}

const RFID_FALL_ARTEN = {
  WECHSEL: { label: "Wechsel", farbe: THEME.colors.warning, hinweis: "Mitglied hat schon eine andere RFID" },
  IDENTITAET_UNKLAR: { label: "Identität unklar", farbe: THEME.colors.accentPurple, hinweis: "CARE-Profil lässt sich keinem Mitglied sicher zuordnen" },
  NICHT_BERECHTIGT: { label: "Nicht berechtigt", farbe: THEME.colors.danger, hinweis: "Person darf aktuell keinen Zutritt haben" },
  RFID_KONFLIKT: { label: "RFID-Konflikt", farbe: THEME.colors.danger, hinweis: "Nummer gehört bereits einer anderen Person" },
  TTLOCK_ABWEICHUNG: { label: "TTLock-Abweichung", farbe: THEME.colors.accentOrange, hinweis: "TTLock-Karte passt nicht zu ComeFit" },
  TYP_NICHT_UNTERSTUETZT: { label: "Typ nicht unterstützt", farbe: THEME.colors.textMuted, hinweis: "Medientyp wird nicht automatisch verarbeitet" },
  NICHT_AUTOMATIKFAEHIG: { label: "Nicht automatikfähig", farbe: THEME.colors.textMuted, hinweis: "Fall kann nur von Hand gelöst werden" },
  TTLOCK_FEHLT: { label: "TTLock fehlt", farbe: THEME.colors.accentCyan, hinweis: "Karte ist in TTLock nicht vorhanden" },
};
const RFID_FALL_STATUS = { offen: "Offen", ausgefuehrt: "Ersetzt", verworfen: "Verworfen", erledigt: "Erledigt", veraltet: "Veraltet", fehler: "Fehler" };

function rfidZeitText(iso) {
  if (!iso) return "–";
  const d = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return "–";
  return `${d.toLocaleString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr`;
}
function rfidDatumText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "–";
}
function rfidJaNeinText(v) {
  if (v === true) return { text: "ja", farbe: THEME.colors.success };
  if (v === false) return { text: "nein", farbe: THEME.colors.textSecondary };
  return { text: "unbekannt", farbe: THEME.colors.textMuted };
}
async function rfidFehlertext(res, fallback) {
  if (res.status === 403) return "Nur für Administratoren.";
  const daten = await res.json().catch(() => ({}));
  return typeof daten.detail === "string" && daten.detail ? daten.detail : fallback;
}

function RfidArtEtikett({ art }) {
  const a = RFID_FALL_ARTEN[art] || { label: art, farbe: THEME.colors.textMuted };
  return (
    <span title={a.hinweis || ""} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: THEME.fontSize.xs, fontWeight: 700, color: a.farbe, border: `1px solid ${a.farbe}`, background: `${a.farbe}22`, whiteSpace: "nowrap" }}>{a.label}</span>
  );
}

export default function RfidPruefliste({ onPersonOeffnen }) {
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const [status, setStatus] = useState(null);
  const [statusFehler, setStatusFehler] = useState("");
  const [faelle, setFaelle] = useState(null);
  const [fehler, setFehler] = useState("");
  const [laedt, setLaedt] = useState(true);
  const [filterStatus, setFilterStatus] = useState("offen");
  const [filterArt, setFilterArt] = useState("");
  const [meldung, setMeldung] = useState(null); // { typ: "ok" | "fehler", text }
  // dialog: { fall, entscheidung, pruefung, pruefungLaedt, pruefungFehler, sendet, fehler }
  const [dialog, setDialog] = useState(null);
  const ladeZaehler = useRef(0);

  const laden = useCallback(async () => {
    const nr = ++ladeZaehler.current;
    setLaedt(true);
    setFehler("");
    try {
      const params = new URLSearchParams({ status: filterStatus });
      if (filterArt) params.set("art", filterArt);
      const [resFaelle, resStatus] = await Promise.all([
        apiFetch(`${API}/api/care-sync/bestand/faelle?${params.toString()}`),
        apiFetch(`${API}/api/care-sync/bestand/status`),
      ]);
      if (nr !== ladeZaehler.current) return;
      if (resFaelle.ok) setFaelle(await resFaelle.json());
      else { setFaelle(null); setFehler(await rfidFehlertext(resFaelle, "Die Fälle konnten nicht geladen werden.")); }
      if (resStatus.ok) { setStatus(await resStatus.json()); setStatusFehler(""); }
      else { setStatus(null); setStatusFehler(await rfidFehlertext(resStatus, "Der Automatik-Status konnte nicht geladen werden.")); }
    } catch {
      if (nr !== ladeZaehler.current) return;
      setFaelle(null);
      setFehler("Keine Verbindung zum Server.");
    }
    if (nr === ladeZaehler.current) setLaedt(false);
  }, [filterStatus, filterArt]);

  useEffect(() => { laden(); }, [laden]);

  const ladePruefung = async (fall) => {
    setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefungLaedt: true, pruefungFehler: "" } : d);
    try {
      const res = await apiFetch(`${API}/api/care-sync/bestand/faelle/${fall.id}/pruefung`);
      if (!res.ok) {
        const text = await rfidFehlertext(res, "Die Prüfung ist fehlgeschlagen.");
        setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefung: null, pruefungLaedt: false, pruefungFehler: text } : d);
        return;
      }
      const pruefung = await res.json();
      setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefung, pruefungLaedt: false } : d);
    } catch {
      setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefung: null, pruefungLaedt: false, pruefungFehler: "Keine Verbindung zum Server." } : d);
    }
  };

  const oeffneDialog = (fall, entscheidung) => {
    setMeldung(null);
    setDialog({ fall, entscheidung, pruefung: null, pruefungLaedt: entscheidung === "ersetzen", pruefungFehler: "", sendet: false, fehler: "" });
    if (entscheidung === "ersetzen") ladePruefung(fall);
  };

  const schliesseDialog = () => { if (dialog && !dialog.sendet) setDialog(null); };

  const entscheiden = async () => {
    if (!dialog || dialog.sendet) return;
    const { fall, entscheidung } = dialog;
    setDialog(d => ({ ...d, sendet: true, fehler: "" }));
    try {
      const res = await apiFetch(`${API}/api/care-sync/bestand/faelle/${fall.id}/entscheiden`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entscheidung }),
      });
      if (!res.ok) {
        const text = await rfidFehlertext(res, "Die Entscheidung konnte nicht ausgeführt werden.");
        setDialog(d => d ? { ...d, sendet: false, fehler: res.status === 409 ? `Nicht ausgeführt: ${text}` : text } : d);
        laden();
        return;
      }
      const erfolgText = { ersetzen: "Neues Band übernommen. Die alte Karte bleibt in TTLock bestehen.", verwerfen: "Fall verworfen. Es wurde nichts geändert.", erledigt: "Fall als erledigt markiert." }[entscheidung];
      setDialog(null);
      setMeldung({ typ: "ok", text: `${erfolgText} (${fall.person?.name || fall.care_name || `Fall ${fall.id}`})` });
      laden();
    } catch {
      setDialog(d => d ? { ...d, sendet: false, fehler: "Keine Verbindung zum Server." } : d);
    }
  };

  const offeneProArt = status?.offene_faelle_nach_art || {};
  const offeneGesamt = Object.values(offeneProArt).reduce((a, b) => a + b, 0);
  const stufeFarbe = status?.stufe_effektiv === "aus" ? THEME.colors.textMuted : THEME.colors.success;
  const btnKlein = { ...st.btn, width: mobil ? "100%" : "auto", fontSize: THEME.fontSize.sm, padding: "9px 14px" };
  const btnKleinGrau = { ...st.btnGray, width: mobil ? "100%" : "auto", fontSize: THEME.fontSize.sm, padding: "9px 14px" };

  const personZelle = (f) => (
    <div style={{ minWidth: 0 }}>
      {f.person && onPersonOeffnen ? (
        <span onClick={() => onPersonOeffnen(f.person.typ, f.person.id)} title="Profil öffnen"
          style={{ color: THEME.colors.primary, fontWeight: 600, cursor: "pointer", textDecoration: "underline", wordBreak: "break-word" }}>{f.person.name}</span>
      ) : (
        <span style={{ color: THEME.colors.text, fontWeight: 600, wordBreak: "break-word" }}>{f.person ? f.person.name : "Kein Mitglied zugeordnet"}</span>
      )}
      {f.person && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>
          {f.person.typ === "mitglied" ? "Mitglied" : f.person.typ} #{f.person.id}{f.person.status ? ` · ${f.person.status}` : ""}{f.person.vertragsbeginn ? ` · seit ${rfidDatumText(f.person.vertragsbeginn)}` : ""}
        </p>
      )}
      {f.care_name && (!f.person || f.care_name !== f.person.name) && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>CARE: {f.care_name}</p>
      )}
    </div>
  );

  const fallZelle = (f) => (
    <div>
      <RfidArtEtikett art={f.art} />
      <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>Zugeordnet: {rfidZeitText(f.zugeordnet)}</p>
      {f.status !== "offen" && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>Status: {RFID_FALL_STATUS[f.status] || f.status}</p>}
    </div>
  );

  const rfidZelle = (f) => {
    const alt = rfidJaNeinText(f.ttlock_alt_vorhanden);
    const neu = rfidJaNeinText(f.ttlock_neu_vorhanden);
    const zeile = { color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 4px", wordBreak: "break-word" };
    return (
      <div>
        <p style={zeile}>Bisherige RFID: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{f.bisherige_rfid_maskiert || (f.person?.rfid_maskiert) || "–"}</span></p>
        <p style={zeile}>Neues CARE-Band: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{f.neues_band_maskiert || "–"}</span></p>
        <p style={{ ...zeile, fontSize: THEME.fontSize.xs, color: THEME.colors.textMuted }}>
          Alte Karte in TTLock: <span style={{ color: alt.farbe, fontWeight: 600 }}>{alt.text}</span> · Neue Karte in TTLock: <span style={{ color: neu.farbe, fontWeight: 600 }}>{neu.text}</span>
        </p>
      </div>
    );
  };

  const gruendeZelle = (f) => (
    f.gruende && f.gruende.length > 0 ? (
      <ul style={{ margin: 0, paddingLeft: 18, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
        {f.gruende.map((g, i) => <li key={i} style={{ marginBottom: 2, wordBreak: "break-word" }}>{g}</li>)}
      </ul>
    ) : <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Gründe hinterlegt.</span>
  );

  const aktionZelle = (f) => (
    f.entscheidungen && f.entscheidungen.length > 0 ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {f.entscheidungen.includes("ersetzen") && <button onClick={() => oeffneDialog(f, "ersetzen")} style={btnKlein}>Neues Band übernehmen (ersetzen)</button>}
        {f.entscheidungen.includes("erledigt") && <button onClick={() => oeffneDialog(f, "erledigt")} style={btnKlein}>Als erledigt markieren</button>}
        {f.entscheidungen.includes("verwerfen") && <button onClick={() => oeffneDialog(f, "verwerfen")} style={btnKleinGrau}>Verwerfen</button>}
      </div>
    ) : (
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>
        {f.entschieden_von ? `Entschieden von ${f.entschieden_von}` : "Keine Entscheidung offen"}{f.ergebnis ? ` – ${f.ergebnis}` : ""}
      </p>
    )
  );

  const spaltenRaster = "1.2fr 1.1fr 1.3fr 1.6fr 1.2fr";
  const dialogTexte = {
    ersetzen: { titel: "Neues Band übernehmen?", knopf: "Jetzt ersetzen", knopfLaedt: "Ersetze…" },
    verwerfen: { titel: "Fall verwerfen?", knopf: "Verwerfen", knopfLaedt: "Verwerfe…" },
    erledigt: { titel: "Fall als erledigt markieren?", knopf: "Als erledigt markieren", knopfLaedt: "Speichere…" },
  };

  return (
    <div>
      {/* Kopfzeile: Status der Bestands-Automatik */}
      <div style={{ ...st.karte, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>🪪 RFID-Prüfliste</h3>
          <button onClick={laden} disabled={laedt} style={{ ...st.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>{laedt ? "Lädt…" : "↻ Aktualisieren"}</button>
        </div>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, margin: "0 0 14px" }}>
          Fälle aus der CARE-Bestands-Automatik, die nicht automatisch entschieden werden. Hier entscheidest du von Hand. Es werden nur maskierte RFID-Nummern angezeigt.
        </p>
        {statusFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>{statusFehler}</p>}
        {status && (
          <>
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, display: "grid", gridTemplateColumns: mobil ? "1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Stufe: <strong style={{ color: stufeFarbe }}>{status.stufe_effektiv}</strong></p>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Aktiv seit: {status.aktiv_ab ? rfidZeitText(status.aktiv_ab) : "noch nicht aktiviert"}</p>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Globaler Modus: <strong>{status.globaler_modus ?? "–"}</strong></p>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Offene Fälle: <strong style={{ color: offeneGesamt > 0 ? THEME.colors.warning : THEME.colors.success }}>{offeneGesamt}</strong></p>
            </div>
            {status.stufe_effektiv !== "ttlock" && (
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>
                Hinweis: „Neues Band übernehmen" ist nur möglich, wenn die Bestands-Automatik auf Stufe „ttlock" steht. Verwerfen und „Als erledigt markieren" gehen immer.
              </p>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.keys(RFID_FALL_ARTEN).filter(a => offeneProArt[a] > 0).map(a => (
                <span key={a} onClick={() => setFilterArt(filterArt === a ? "" : a)} title="Nach dieser Art filtern"
                  style={{ cursor: "pointer", opacity: filterArt && filterArt !== a ? 0.5 : 1 }}>
                  <RfidArtEtikett art={a} /> <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm, fontWeight: 600 }}>{offeneProArt[a]}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Filter */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {[["offen", "Offen"], ["alle", "Alle"]].map(([key, label]) => (
          <button key={key} onClick={() => setFilterStatus(key)}
            style={{ ...st.btnGray, width: "auto", padding: "8px 18px", fontSize: THEME.fontSize.sm, background: filterStatus === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: filterStatus === key ? kontrasttext(THEME.colors.primary) : kontrasttext(THEME.colors.surfaceMuted) }}>{label}</button>
        ))}
        <select value={filterArt} onChange={e => setFilterArt(e.target.value)} aria-label="Art filtern" style={{ ...st.input, width: mobil ? "100%" : "auto", padding: "8px 12px", fontSize: THEME.fontSize.sm }}>
          <option value="">Alle Arten</option>
          {Object.entries(RFID_FALL_ARTEN).map(([key, a]) => <option key={key} value={key}>{a.label}</option>)}
        </select>
      </div>

      {meldung && (
        <div style={{ background: meldung.typ === "ok" ? `${THEME.colors.success}22` : `${THEME.colors.danger}22`, border: `1px solid ${meldung.typ === "ok" ? THEME.colors.success : THEME.colors.danger}`, borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: meldung.typ === "ok" ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.base }}>
          {meldung.typ === "ok" ? "✓ " : ""}{meldung.text}
        </div>
      )}

      {/* Liste */}
      {fehler && (
        <div style={{ ...st.karte, borderLeftColor: THEME.colors.danger }}>
          <p style={{ color: THEME.colors.danger, margin: "0 0 12px" }}>{fehler}</p>
          <button onClick={laden} style={{ ...st.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>Erneut versuchen</button>
        </div>
      )}
      {!fehler && faelle === null && laedt && <p style={{ color: THEME.colors.textMuted, padding: 24, textAlign: "center" }}>Lade RFID-Fälle…</p>}
      {!fehler && faelle !== null && faelle.length === 0 && (
        <div style={{ ...st.karte, textAlign: "center" }}>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, margin: 0 }}>
            {filterStatus === "offen" && !filterArt ? "Keine offenen RFID-Fälle." : "Keine Fälle für diese Auswahl."}
          </p>
        </div>
      )}
      {!fehler && faelle !== null && faelle.length > 0 && (mobil ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, opacity: laedt ? 0.6 : 1 }}>
          {faelle.map(f => (
            <div key={f.id} style={{ ...st.karte, padding: "1.1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start", marginBottom: 10 }}>
                {personZelle(f)}
                <RfidArtEtikett art={f.art} />
              </div>
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>Zugeordnet: {rfidZeitText(f.zugeordnet)}{f.status !== "offen" ? ` · ${RFID_FALL_STATUS[f.status] || f.status}` : ""}</p>
              <div style={{ marginBottom: 10 }}>{rfidZelle(f)}</div>
              <div style={{ marginBottom: 12 }}>{gruendeZelle(f)}</div>
              {aktionZelle(f)}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...st.tabelle, opacity: laedt ? 0.6 : 1 }}>
          <div style={{ ...st.tabelleHeader, gridTemplateColumns: spaltenRaster, gap: 16 }}>
            <div>Mitglied</div><div>Fall</div><div>RFID</div><div>Gründe</div><div>Entscheidung</div>
          </div>
          {faelle.map(f => (
            <div key={f.id} style={{ ...st.tabelleZeile, gridTemplateColumns: spaltenRaster, gap: 16, alignItems: "start" }}>
              {personZelle(f)}
              {fallZelle(f)}
              {rfidZelle(f)}
              {gruendeZelle(f)}
              {aktionZelle(f)}
            </div>
          ))}
        </div>
      ))}

      {/* Bestaetigungsdialog (eigenes Modal, kein window.confirm - in der PWA unzuverlaessig) */}
      {dialog && (
        <div style={st.modalBg} onClick={schliesseDialog}>
          <div style={{ ...st.modal, width: "min(560px, 100%)", padding: mobil ? "1.4rem" : "2rem", boxSizing: "border-box" }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 style={{ color: THEME.colors.text, margin: "0 0 6px", fontSize: THEME.fontSize.xl }}>{dialogTexte[dialog.entscheidung].titel}</h3>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 14px" }}>
              {dialog.fall.person?.name || dialog.fall.care_name || `Fall ${dialog.fall.id}`} · <RfidArtEtikett art={dialog.fall.art} />
            </p>
            {dialog.entscheidung === "ersetzen" ? (
              <>
                <div style={{ background: `${THEME.colors.warning}18`, border: `1px solid ${THEME.colors.warning}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>
                    Die neue RFID wird beim Mitglied eingetragen und es wird eine neue TTLock-Karte angelegt. Die alte Karte bleibt in TTLock bestehen und muss bei Bedarf von Hand entfernt werden.
                  </p>
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>
                  Bisherige RFID: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{dialog.fall.bisherige_rfid_maskiert || "–"}</span> → Neues Band: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{dialog.fall.neues_band_maskiert || "–"}</span>
                </p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "14px 0 6px" }}>Prüfpunkte</p>
                {dialog.pruefungLaedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Prüfe…</p>}
                {dialog.pruefungFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{dialog.pruefungFehler}</p>}
                {dialog.pruefung && (
                  <div style={{ background: THEME.colors.background, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                    {dialog.pruefung.punkte.map((p, i) => (
                      <p key={i} style={{ color: p.ok ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "3px 0" }}>{p.ok ? "✓" : "✗"} {p.pruefung}</p>
                    ))}
                    {!dialog.pruefung.moeglich && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "8px 0 0" }}>Ersetzen ist erst möglich, wenn alle Prüfpunkte grün sind.</p>}
                  </div>
                )}
                <button onClick={() => ladePruefung(dialog.fall)} disabled={dialog.pruefungLaedt || dialog.sendet} style={{ ...st.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 12px", marginBottom: 6 }}>↻ Neu prüfen</button>
              </>
            ) : (
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 12px" }}>
                {dialog.entscheidung === "verwerfen"
                  ? "Das CARE-Medium wird nicht übernommen. Es wird nichts in ComeFit oder TTLock geändert, der Fall wird geschlossen."
                  : "Du hast den Fall von Hand gelöst. Es wird nichts in ComeFit oder TTLock geändert, der Fall wird geschlossen."}
              </p>
            )}
            {dialog.fehler && (
              <div style={{ background: `${THEME.colors.danger}22`, border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: "10px 14px", margin: "10px 0", color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{dialog.fehler}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
              <button onClick={schliesseDialog} disabled={dialog.sendet} style={{ ...st.btnGray, width: mobil ? "100%" : "auto", fontSize: THEME.fontSize.base, padding: "10px 18px" }}>Abbrechen</button>
              <button onClick={entscheiden}
                disabled={dialog.sendet || (dialog.entscheidung === "ersetzen" && !(dialog.pruefung && dialog.pruefung.moeglich))}
                style={{ ...st.btn, width: mobil ? "100%" : "auto", fontSize: THEME.fontSize.base, padding: "10px 18px", opacity: (dialog.sendet || (dialog.entscheidung === "ersetzen" && !(dialog.pruefung && dialog.pruefung.moeglich))) ? 0.5 : 1, cursor: dialog.sendet ? "wait" : "pointer" }}>
                {dialog.sendet ? dialogTexte[dialog.entscheidung].knopfLaedt : dialogTexte[dialog.entscheidung].knopf}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

